import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  ArrowLeft,
  BadgeCheck,
  Building2,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  ImagePlus,
  Info,
  Loader2,
  Minus,
  Plus,
  Search,
  Upload,
  Users,
  X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "@/components/Modal";
import SocietyAvatar from "@/components/SocietyAvatar";
import { getEarliestSelectableDateString, isValidActivityDate } from "@/utils/deadline";
import {
  isAuthEnvelopeStatus,
  redeemSponsorFailureKey,
  redeemSponsorMessageKey,
  redeemSponsorServerMessage,
} from "@/utils/redeemSponsorStatus";
import StudentSponsorAgreementModal from "@/components/student/StudentSponsorAgreementModal";
import { universityList } from "@/assets/universityList";
import { cloudinaryUpload } from "@/services/middleware/uploadImage";
import { getStudentProfileAPI } from "@/services/api/user/getStudentProfile";
import { UploadStudentDocsAPI } from "@/services/api/user/uploadStudentDocs";
import {
  GetPendingSocietiesAPI,
  GetUserSocietiesAPI,
  type UserSociety,
} from "@/services/api/society/getUserSocieties";
import {
  SearchSocietiesAPI,
  type SearchSociety,
} from "@/services/api/society/searchSocieties";
import { RegisterSocietyAPI } from "@/services/api/society/registerSociety";
import { UploadSocietyChopAPI } from "@/services/api/society/uploadSocietyChop";
import { RedeemSponsorAPI } from "@/services/api/sponsor/redeemSponsor";
import { GetSponsorTaskRequirementsAPI } from "@/services/api/sponsor/taskRequirements";
import { editUserInfo } from "@/services/slices/auth.slice";
import type { SponsorItem } from "@/services/api/sponsor/getSponsors";
import type { AgreementSponsorTask } from "@/data/sponsorshipAgreement";
import { describeSponsorTaskLine } from "@/data/sponsorshipAgreement";
import {
  MIN_COMMITTEE_IGS,
  MAX_COMMITTEE_IGS,
  MAX_PARTICIPANTS,
  MIN_AVAILABLE_SPONSOR_TASKS,
  computeMaxQuantity,
} from "@/constants/sponsorRedeem";

type RedeemType = "personal" | "society";
type Step =
  | "type"
  | "uploadDocs"
  | "societyPicker"
  | "societyRegistration"
  | "activity"
  | "quantity"
  | "agreement";

type SponsorSociety = (UserSociety | SearchSociety) & { membership_id?: string };

interface StudentSponsorRedeemModalProps {
  visible: boolean;
  onClose: () => void;
  /** Called with the API status so the page can show the mobile error text. */
  onRedeemed: (status?: number) => void;
  sponsor: SponsorItem | null;
  /** Upper bound of the self-reported participant count. */
  maxParticipants?: number;
  /** Mirrors the participant count into the sponsor card's own stepper. */
  onParticipantsChange?: (participants: number) => void;
  /** Participants already picked on the sponsor card, if any. */
  cardParticipants?: number | null;
  /** Quantity already picked on the sponsor card, if any. */
  quantity?: number | null;
  onQuantityChange?: (quantity: number) => void;
}

const emptyCommitteeIGs = () => Array.from({ length: MIN_COMMITTEE_IGS }, () => "");

/**
 * Client-side page size for the "All Societies" directory (1,228 rows in the
 * database). The list renders inside a `max-h-56` (224px) scroll box that fits
 * ~3.5 rows at the 60px row height, so 20 rows is a comfortable ~5 scroll
 * gestures per page while collapsing the directory to 62 pages instead of
 * 1,228 rows. Search is the primary narrowing tool; pages only guard against
 * having to render all 1,228 at once.
 */
const SOCIETY_PAGE_SIZE = 20;

/**
 * How long the declared inputs have to hold still before the sponsor tasks are
 * re-resolved for the "Sponsor Tasks" list.
 *
 * Long enough that typing a multi-digit participant count costs one request
 * rather than one per keystroke, short enough that the count in the list reads
 * as live rather than as a value the student has already typed past.
 */
const TASK_REQUIREMENTS_DEBOUNCE_MS = 400;

/**
 * Web port of the RN `RedeemTypeModal`: pick personal / society, pass the
 * student-profile verification gate, join or register a society, fill in the
 * activity details (name, date, official IG, 5–10 committee IGs) and sign the
 * sponsorship agreement before `redeemSponsor` is called.
 */
const StudentSponsorRedeemModal: React.FC<StudentSponsorRedeemModalProps> = ({
  visible,
  onClose,
  onRedeemed,
  sponsor,
  maxParticipants = MAX_PARTICIPANTS,
  onParticipantsChange,
  cardParticipants,
  quantity,
  onQuantityChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const student = useSelector((state: any) => state.auth?.student);

  const [step, setStep] = useState<Step>("type");
  const [redeemType, setRedeemType] = useState<RedeemType>("society");
  const [warning, setWarning] = useState("");
  const [busy, setBusy] = useState(false);

  const [chopUploading, setChopUploading] = useState(false);

  const [activityName, setActivityName] = useState("");
  const [activityDate, setActivityDate] = useState("");
  const [participants, setParticipants] = useState("1");
  const [officialIG, setOfficialIG] = useState("");
  const [committeeIGs, setCommitteeIGs] = useState<string[]>(emptyCommitteeIGs);

  const [allSocieties, setAllSocieties] = useState<SearchSociety[]>([]);
  const [mySocieties, setMySocieties] = useState<UserSociety[]>([]);
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [societyQuery, setSocietyQuery] = useState("");
  /** 1-based page of the client-side "All Societies" directory. */
  const [societyPage, setSocietyPage] = useState(1);
  const societyListRef = useRef<HTMLDivElement>(null);
  const [selectedSociety, setSelectedSociety] = useState<SponsorSociety | null>(null);
  const [pendingJoinSociety, setPendingJoinSociety] = useState<SearchSociety | null>(null);
  const [pendingJoinOnly, setPendingJoinOnly] = useState(false);
  const [joiningSocietyId, setJoiningSocietyId] = useState<string | null>(null);

  const [regName, setRegName] = useState("");
  const [regUniversity, setRegUniversity] = useState("");
  const [regInstagram, setRegInstagram] = useState("");
  const [regIcon, setRegIcon] = useState("");
  const [regHkid, setRegHkid] = useState("");
  const [regStudentCard, setRegStudentCard] = useState("");
  const [regChop, setRegChop] = useState("");
  const [regSubmitted, setRegSubmitted] = useState(false);
  const [regVerified, setRegVerified] = useState(false);

  const [hkidImage, setHkidImage] = useState("");
  const [studentCardImage, setStudentCardImage] = useState("");

  // The agreement is a step in the same machine as the other screens, mirroring
  // the RN modal (`showAgreement = step === 'agreement'`), rather than a second
  // boolean layered over a separate `step`. One source of truth for "where am
  // I" means the Back/reset logic cannot desync from what is on screen.
  const showAgreement = step === "agreement";
  const setShowAgreement = (value: boolean) =>
    setStep(value ? "agreement" : "activity");

  useEffect(() => {
    if (student?.university) setRegUniversity(String(student.university));
  }, [student?.university]);

  // Open the modal already in sync with whatever the sponsor card holds, so
  // editing there is not silently discarded.
  useEffect(() => {
    if (!visible) return;
    if (cardParticipants !== null && cardParticipants !== undefined) {
      setParticipants(String(cardParticipants));
    }
    if (quantity !== null && quantity !== undefined) setQuantityChoice(quantity);
  }, [visible, cardParticipants, quantity]);

  useEffect(() => {
    if (!visible) return;
    // Personal redemption is hidden while the society flow is the focus, so the
    // modal opens straight on the society picker instead of offering a choice.
    // `redeemType` starts as "society" rather than "personal" so no path can reach
    // the personal payload by default.
    setRedeemType("society");
    setWarning("");
    setActivityName("");
    setActivityDate("");
    setParticipants("1");
    setOfficialIG("");
    setCommitteeIGs(emptyCommitteeIGs());
    setSelectedSociety(null);
    setAllSocieties([]);
    setMySocieties([]);
    setPendingIds([]);
    setSocietyQuery("");
    setSocietyPage(1);
    setPendingJoinSociety(null);
    setPendingJoinOnly(false);
    setRegName("");
    setRegInstagram("");
    setRegIcon("");
    setRegHkid("");
    setRegStudentCard("");
    setRegChop("");
    setRegSubmitted(false);
    setRegVerified(false);
    setHkidImage("");
    setStudentCardImage("");
    setAgreementTasks([]);
    setPreviewTasks([]);
    setSelectedTaskIds(null);
    // Set synchronously so the picker is what renders on open, rather than the
    // hidden type step flashing until the directory request resolves.
    setStep("societyPicker");

    // Load the society directory and land on the picker. Same work the (now hidden)
    // type step used to do when "Society" was pressed.
    let cancelled = false;
    (async () => {
      try {
        const [all, mine, pending] = await Promise.all([
          SearchSocietiesAPI(),
          GetUserSocietiesAPI(),
          GetPendingSocietiesAPI(),
        ]);
        if (cancelled) return;
        setAllSocieties(all);
        setMySocieties(mine);
        setPendingIds(pending.map((society) => society.id));
        setSocietyQuery("");
        setSocietyPage(1);
        setStep("societyPicker");
      } catch {
        if (cancelled) return;
        setStep("societyPicker");
        setWarning(t("Failed to load societies. Please try again."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // Closing the modal drops the explicit selection, so reopening a sponsor (or
  // a different one) starts again from "all currently available tasks".
  useEffect(() => {
    if (!visible) setSelectedTaskIds(null);
  }, [visible]);

  // Kept as task objects, not bare titles: the activity step lists what the
  // sponsor asks for, and the title is all it needs.
  const sponsorTasks = useMemo(
    () =>
      (sponsor?.tasks ?? []).map((task) => ({
        title: String(task?.title ?? ""),
      })),
    [sponsor],
  );

  /**
   * The same tasks as the agreement needs them, with each obligation resolved by
   * the server for the participant count this redemption declares.
   *
   * The catalog copy of a task cannot be used here: it is resolved with no
   * participants, so its `follower_count` is the formula's floor, while
   * `reviewTask` holds the student to the count scaled from the number they
   * declared. Empty until `handleQuantitySubmit` has fetched it, and the
   * agreement step is only entered once that has succeeded, so the signed
   * document never carries an unresolved number.
   */
  const [agreementTasks, setAgreementTasks] = useState<AgreementSponsorTask[]>([]);

  /**
   * Tasks for the on-screen list, resolved for the participant count currently
   * typed, so the obligation shown next to each task updates as the count
   * changes instead of showing the formula's floor.
   *
   * Separate from `agreementTasks` because this one is a live preview: it is
   * debounced and tolerates failure, falling back to the catalog copy (which
   * states the rule without a number) rather than blocking the form. The
   * agreement step still blocks, because only the signed document has to be
   * exact.
   */
  const [previewTasks, setPreviewTasks] = useState<AgreementSponsorTask[]>([]);
  const previewRequestRef = useRef(0);
  const [requirementsLoading, setRequirementsLoading] = useState(false);

  /**
   * The student's explicit task selection. `null` means "all tasks that are
   * currently available", so the default keeps following the availability list
   * until the student touches a checkbox. Once it is an array, tasks that stop
   * being available are filtered out but new tasks are never added silently.
   */
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[] | null>(null);

  const displayedTasks: any[] =
    previewTasks.length > 0 ? previewTasks : sponsorTasks;
  const availableTasks = displayedTasks.filter(
    (task) => task.available !== false,
  );
  const availableCount = availableTasks.length;
  const availableTaskIds = availableTasks
    .filter((task) => task.id)
    .map((task) => String(task.id));
  const effectiveSelectedTaskIds =
    selectedTaskIds === null
      ? availableTaskIds
      : selectedTaskIds.filter((id) => availableTaskIds.includes(id));
  /**
   * The catalog fallback (before the first preview response, or after a failed
   * one) carries titles but no ids, so there is nothing to tick yet. In that
   * state the selection gate is not meaningful; the activity submit re-resolves
   * the tasks with ids and applies it then.
   */
  const tasksHaveIds =
    availableTasks.length > 0 &&
    availableTasks.every((task) => Boolean(task.id));
  const selectedCount = tasksHaveIds
    ? effectiveSelectedTaskIds.length
    : availableCount;
  const availabilityWarning = (() => {
    if (availableCount >= MIN_AVAILABLE_SPONSOR_TASKS) return "";
    const unavailable = displayedTasks.filter(
      (task) => task.available === false,
    );
    const followUnavailable = unavailable.some(
      (task) => task.type === "follow_and_like_post",
    );
    const conflict = unavailable
      .flatMap((task) =>
        (task.unavailable_conflicts ?? []).map((item: any) => ({
          ...item,
          type: task.type,
        })),
      )
      .find((item) => item.type === "follow_and_like_post");
    const handles = (conflict?.handles ?? [])
      .map((handle: string) => `@${handle}`)
      .join(", ");
    if (followUnavailable && handles) {
      return t(
        "Only {{count}} of 4 required sponsor tasks are available. Follow & Like Post is unavailable because {{handles}} were already used for {{sponsor}}. Please use different committee accounts.",
        {
          count: availableCount,
          handles,
          sponsor: conflict?.sponsor_name || "",
        },
      );
    }
    return t("Only {{count}} of 4 required sponsor tasks are available.", {
      count: availableCount,
    });
  })();

  const handleToggleTask = (taskId?: string) => {
    if (!taskId || !availableTaskIds.includes(taskId)) return;
    const base =
      selectedTaskIds === null ? availableTaskIds : effectiveSelectedTaskIds;
    setSelectedTaskIds(
      base.includes(taskId)
        ? base.filter((id) => id !== taskId)
        : [...base, taskId],
    );
  };

  /** `null` while the typed participant count is not a usable integer. */
  const participantsQuantity = useMemo(() => {
    const trimmed = participants.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const value = Number(trimmed);
    return Number.isInteger(value) && value >= 1 && value <= maxParticipants
      ? value
      : null;
  }, [participants, maxParticipants]);

  /**
   * The typed participant count, accepted for preview even when the form will not
   * submit it.
   *
   * Two different things are going on and conflating them is what made the count
   * read wrong:
   *
   * - `participantsQuantity` decides whether the count may be *submitted*. It is
   *   null above the bound, and that must keep blocking the form.
   * - The task list *displays* the declared headcount, which should track what was
   *   typed. Showing nothing (or a clamped value) for a count the student typed
   *   would misreport it, so the display uses its own count.
   *
   * The follower requirement scales proportionally, so a larger count genuinely
   * states a larger obligation and there is no server-side ceiling flattening it.
   */
  const previewParticipants = useMemo(() => {
    const trimmed = participants.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const value = Number(trimmed);
    return value >= 1 ? value : null;
  }, [participants]);

  /**
   * The count sent to the preview endpoint.
   *
   * The endpoint rejects a participant count larger than the `participants_quantity`
   * column can hold, so an out-of-column preview would come back as an error and the
   * list would show nothing at all. The request is therefore clamped to that ceiling.
   *
   * This is the storage limit, not a product rule: there is no longer a 500 cap, so
   * any count a student could legitimately declare is previewed at its own value and
   * the follower clause scales with it.
   *
   * The declared headcount the title renders is not taken from this response. See
   * `previewTasks` below.
   */
  const previewRequestCount = useMemo(
    () =>
      previewParticipants === null
        ? null
        : Math.min(previewParticipants, maxParticipants),
    [previewParticipants, maxParticipants],
  );

  // Live preview of the obligation, debounced so typing a count does not fire a
  // request per keystroke. The request id discards out-of-order replies: a slow
  // response for an earlier count must never overwrite a newer one, or the list
  // would show a number the student did not ask for.
  useEffect(() => {
    if (!visible || !sponsor || previewRequestCount === null) {
      setPreviewTasks([]);
      return;
    }
    const sponsorId = sponsor.id;
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    const timer = setTimeout(async () => {
      const tasks = await GetSponsorTaskRequirementsAPI({
        sponsorId,
        participants: previewRequestCount,
        type: redeemType,
        officialIG,
        committeeIGs:
          redeemType === "society"
            ? committeeIGs
                .map((ig) => ig.trim())
                .filter((ig) => ig && ig !== "@")
            : undefined,
      });
      if (previewRequestRef.current !== requestId) return;
      // The headcount in the title is the number the student typed, not the
      // clamped number the request had to send. The server echoes the count it was
      // given, so it is restated here rather than inherited; otherwise a typed 1000
      // would preview as 500.
      const declared = previewParticipants;
      setPreviewTasks(
        declared === null || !tasks
          ? tasks || []
          : tasks.map((task) => ({
              ...task,
              requirement: task.requirement
                ? { ...task.requirement, participants: declared }
                : task.requirement,
            })),
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [
    visible,
    sponsor,
    previewRequestCount,
    previewParticipants,
    redeemType,
    officialIG,
    committeeIGs,
  ]);

  /**
   * The value cap: `ceil(participants x HK$1 / price)` in packs as priced.
   * 0 while the participant count is still invalid, which disables the picker.
   */
  const maxQuantity = computeMaxQuantity(
    participantsQuantity ?? 0,
    sponsor?.price,
  );

  /** The student's own pick, held inside [1, maxQuantity]. */
  const [quantityChoice, setQuantityChoice] = useState(1);
  const pickedQuantity =
    quantity !== null && quantity !== undefined ? quantity : quantityChoice;
  const effectiveQuantity =
    maxQuantity > 0
      ? Math.min(Math.max(1, pickedQuantity), maxQuantity)
      : 0;

  const handleQuantityChange = (next: number) => {
    const value = Math.min(Math.max(1, next), Math.max(1, maxQuantity));
    setQuantityChoice(value);
    onQuantityChange?.(value);
  };

  const handleParticipantsChange = (raw: string) => {
    const digits = raw.replace(/[^\d]/g, "").slice(0, 6);
    setParticipants(digits);
    const value = Number(digits);
    if (Number.isInteger(value) && value >= 1) {
      onParticipantsChange?.(value);
    }
  };

  /**
   * RN `useIsStudentVerifiedPersonal`: refreshes the student slice from the
   * profile API and classifies the verification state. `verified` is the
   * single source of truth; documents only move the student to "pending".
   */
  const checkPersonalVerification = async (): Promise<
    "allow_upload" | "wait_verification" | "verified"
  > => {
    try {
      const resp = await getStudentProfileAPI();
      const profile = resp?.result;
      if (profile) {
        dispatch(
          editUserInfo({
            userType: "student",
            user: {
              ...(student ?? {}),
              id: student?.id,
              profile_icon: profile.profile_icon || "",
              verified: profile.verified ?? false,
              student_card_image_url: profile.student_card_image_url || "",
              student_card_number: profile.student_card_number || "",
              student_card_name: profile.student_card_name || "",
              hkid_image_url: profile.hkid_image_url || "",
              hkid_number: profile.hkid_number || "",
              hkid_name: profile.hkid_name || "",
              society_id: student?.society_id || [],
              user_auth: student?.user_auth || "",
            },
          }),
        );
        if (!profile.verified) {
          if (!profile.student_card_image_url || !profile.hkid_image_url) {
            return "allow_upload";
          }
          return "wait_verification";
        }
        return "verified";
      }
      return "allow_upload";
    } catch {
      return "allow_upload";
    }
  };

  const handlePersonalPress = async () => {
    setBusy(true);
    const verifyStatus = await checkPersonalVerification();
    setBusy(false);
    if (verifyStatus === "allow_upload") {
      setStep("uploadDocs");
      return;
    }
    if (verifyStatus === "wait_verification") {
      setWarning(
        t(
          "Personal verification pending. Please wait for UninfoHK to verify your documents.",
        ),
      );
      return;
    }
    setWarning("");
    setRedeemType("personal");
    setStep("activity");
  };

  const handleSocietyPress = async () => {
    setBusy(true);
    setWarning("");
    try {
      const [all, mine, pending] = await Promise.all([
        SearchSocietiesAPI(),
        GetUserSocietiesAPI(),
        GetPendingSocietiesAPI(),
      ]);
      setAllSocieties(all);
      setMySocieties(mine);
      setPendingIds(pending.map((society) => society.id));
      setSocietyQuery("");
      setSocietyPage(1);
      setStep("societyPicker");
    } catch {
      setWarning(t("Failed to load societies. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleSocietySelected = (society: SponsorSociety) => {
    setSelectedSociety(society);
    setRedeemType("society");
    setOfficialIG(
      // `instagram_handle`, not `instagram_url`: the column holds a full profile
      // URL on scraped societies, which pre-filled this field with
      // "@https://instagram.com/…".
      society.instagram_handle ? `@${society.instagram_handle}` : "@",
    );
    setStep("activity");
  };

  const handleJoinSociety = async (society: SearchSociety) => {
    if (pendingIds.length > 0) {
      setWarning(
        t(
          "You already have a pending membership request. Please wait for approval.",
        ),
      );
      return;
    }
    setJoiningSocietyId(society.id);
    setWarning("");
    try {
      const result = await RegisterSocietyAPI({ society_name: society.name });
      if (result && "error" in result) {
        setWarning(result.error);
        return;
      }
      if (result) {
        if (result.already_verified) {
          handleSocietySelected({
            ...society,
            membership_id: result.membership.id,
          } as SponsorSociety);
        } else {
          setPendingIds((prev) => [...prev, result.society.id]);
          const updated = await GetUserSocietiesAPI();
          setMySocieties(updated);
          setWarning(
            t(
              "Membership request sent. Please wait for a society member to approve before you can redeem as a society.",
            ),
          );
        }
      } else {
        setWarning(t("Failed to send membership request. Please try again."));
      }
    } catch {
      setWarning(t("Failed to send membership request. Please try again."));
    } finally {
      setJoiningSocietyId(null);
    }
  };

  const pickImageInto = async (
    file: File | undefined,
    apply: (dataUrl: string) => void,
  ) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => apply(String(reader.result || ""));
    reader.onerror = () => setWarning(t("Failed to read the selected file."));
    reader.readAsDataURL(file);
  };

  const handleUploadChop = async () => {
    if (!selectedSociety) return;
    const societyId = selectedSociety.id;
    setChopUploading(true);
    setWarning("");
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = async (e: any) => {
        const file = e.target.files?.[0];
        if (!file) {
          setChopUploading(false);
          return;
        }
        try {
          const reader = new FileReader();
          reader.onload = async () => {
            const dataUrl = String(reader.result || "");
            try {
              const chopUrl = await cloudinaryUpload(dataUrl);
              if (!chopUrl) {
                setWarning(t("Failed to upload the society chop. Please try again."));
                setChopUploading(false);
                return;
              }
              const resp = await UploadSocietyChopAPI({
                society_id: societyId,
                chop_image_url: chopUrl,
              });
              if (resp?.status !== 0) {
                setWarning(t("Failed to save the society chop. Please try again."));
                setChopUploading(false);
                return;
              }
              const storedChopUrl = resp.result?.chop_image_url || chopUrl;
              setSelectedSociety((prev) =>
                prev && prev.id === societyId
                  ? { ...prev, chop_image_url: storedChopUrl }
                  : prev,
              );
            } catch (err) {
              console.error(err);
              setWarning(t("Failed to upload the society chop. Please try again."));
            } finally {
              setChopUploading(false);
            }
          };
          reader.onerror = () => {
            setWarning(t("Failed to read the selected file."));
            setChopUploading(false);
          };
          reader.readAsDataURL(file);
        } catch (err) {
          console.error(err);
          setWarning(t("Failed to upload the society chop. Please try again."));
          setChopUploading(false);
        }
      };
      input.click();
    } catch (err) {
      console.error(err);
      setWarning(t("Failed to upload the society chop. Please try again."));
      setChopUploading(false);
    }
  };


    const handleRegisterSociety = async () => {
    if (pendingIds.length > 0) {
      setWarning(
        t(
          "You already have a pending membership request. Please wait for approval.",
        ),
      );
      return;
    }
    if (!regName.trim()) {
      setWarning(t("Please enter the society name."));
      return;
    }
    if (!regHkid || !regStudentCard) {
      setWarning(t("Please upload both HKID and student card."));
      return;
    }
    setBusy(true);
    setWarning("");
    try {
      const [hkidUrl, studentCardUrl, chopUrl, iconUrl] = await Promise.all([
        cloudinaryUpload(regHkid),
        cloudinaryUpload(regStudentCard),
        regChop ? cloudinaryUpload(regChop) : Promise.resolve(undefined),
        regIcon ? cloudinaryUpload(regIcon) : Promise.resolve(undefined),
      ]);
      if (!hkidUrl || !studentCardUrl) {
        setWarning(t("Failed to register society. Please try again."));
        return;
      }
      const result = await RegisterSocietyAPI({
        society_name: regName.trim(),
        university: regUniversity.trim() || undefined,
        instagram_url: regInstagram.trim().replace(/^@/, "") || undefined,
        society_icon: iconUrl || undefined,
        hkid_url: hkidUrl,
        student_card_url: studentCardUrl,
        chop_image_url: chopUrl || undefined,
      });
      if (!result) {
        setWarning(t("Failed to register society. Please try again."));
        return;
      }
      if ("error" in result) {
        setWarning(result.error);
        return;
      }
      const verified =
        result.already_verified === true || result.membership?.verified === true;
      setRegSubmitted(true);
      setRegVerified(verified);
      setSelectedSociety({
        membership_id: result.membership.id,
        id: result.society.id,
        name: result.society.name,
        icon: result.society.icon,
        description: null,
        university: result.society.university,
        instagram_handle: result.society.instagram_handle,
        instagram_profile_url: result.society.instagram_profile_url,
        instagram_url: result.society.instagram_url,
        chop_image_url: result.society.chop_image_url || chopUrl || null,
      });
      if (!verified) setPendingIds((prev) => [...prev, result.society.id]);
      if (verified) {
        setRedeemType("society");
        setOfficialIG(
          result.society.instagram_handle
            ? `@${result.society.instagram_handle}`
            : "@",
        );
        setStep("activity");
      }
    } catch {
      setWarning(t("Failed to register society. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleUploadDocs = async () => {
    if (!hkidImage || !studentCardImage) {
      setWarning(t("Please upload both your HKID and student card images."));
      return;
    }
    setBusy(true);
    setWarning("");
    try {
      const [hkidUrl, studentCardUrl] = await Promise.all([
        cloudinaryUpload(hkidImage),
        cloudinaryUpload(studentCardImage),
      ]);
      const resp = await UploadStudentDocsAPI({
        hkidUri: hkidUrl,
        studentCardUri: studentCardUrl,
      });
      if (resp?.status === 0) {
        // Uploading only stores the documents; the account stays unverified
        // until an admin approves, so stop here and surface the pending state.
        setHkidImage("");
        setStudentCardImage("");
        setStep("societyPicker");
        setWarning(
          t(
            "Personal verification pending. Please wait for UninfoHK to verify your documents.",
          ),
        );
        return;
      }
      setWarning(
        resp?.resp || resp?.message || t("Upload failed. Please try again."),
      );
    } catch {
      setWarning(t("Upload failed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleActivitySubmit = async () => {
    if (!activityName.trim() || !activityDate.trim()) {
      setWarning(t("Please fill in both activity name and date."));
      return;
    }
    // Earliest activity date is tomorrow; the input's min is only a UI affordance.
    if (!isValidActivityDate(activityDate)) {
      setWarning(t("Please select a future date (tomorrow or later)."));
      return;
    }
    if (participantsQuantity === null) {
      setWarning(
        t(
          "Please fill in a whole number of participants between 1 and {{max}}.",
          { max: maxParticipants },
        ),
      );
      return;
    }
    if (!officialIG.trim() || officialIG.trim() === "@") {
      setWarning(t("Please fill in the official society Instagram account."));
      return;
    }
    // A personal activity has no society and therefore no committee, so the
    // committee accounts are not asked for and not validated there. The official
    // IG above stays required on both paths: `sponsorRequirements` resolves a
    // `register_app` task from `official_ig_account` for both activity types,
    // and for a personal redemption that account is the student's own.
    if (redeemType === "society") {
      const trimmedIGs = committeeIGs
        .map((ig) => ig.trim())
        .filter((ig) => ig && ig !== "@");
      if (trimmedIGs.length < MIN_COMMITTEE_IGS) {
        setWarning(t("Please fill in at least five committee IG accounts."));
        return;
      }
      const lowerIGs = trimmedIGs.map((ig) => ig.toLowerCase());
      const officialLower = officialIG.trim().toLowerCase();
      const hasDuplicate = lowerIGs.some(
        (ig, idx) => lowerIGs.indexOf(ig) !== idx,
      );
      const hasOfficialDup = lowerIGs.includes(officialLower);
      if (hasDuplicate || hasOfficialDup) {
        setWarning(
          t(
            "Duplicate Instagram accounts found between committee and official. Please ensure all are unique.",
          ),
        );
        return;
      }

    }

    setWarning("");
    setRequirementsLoading(true);
    const committee = committeeIGs
      .map((ig) => ig.trim())
      .filter((ig) => ig && ig !== "@");
    const tasks = await GetSponsorTaskRequirementsAPI({
      sponsorId: sponsor?.id ?? "",
      participants: participantsQuantity,
      type: redeemType,
      officialIG,
      committeeIGs: redeemType === "society" ? committee : undefined,
    });
    setRequirementsLoading(false);
    if (!tasks) {
      setWarning(t("Could not load the sponsor obligations. Please try again."));
      return;
    }
    setPreviewTasks(tasks);
    // Keep the list (and its single warning) on screen when the redemption is
    // blocked. Availability and selection failures belong under that list, not
    // in the bottom warning.
    const available = tasks.filter(
      (task) => task.available !== false && task.id,
    );
    const availableIds = available.map((task) => String(task.id));
    setSelectedTaskIds((prev) =>
      prev === null ? null : prev.filter((id) => availableIds.includes(id)),
    );
    if (available.length < MIN_AVAILABLE_SPONSOR_TASKS) {
      return;
    }
    const selectedIds =
      selectedTaskIds === null
        ? availableIds
        : selectedTaskIds.filter((id) => availableIds.includes(id));
    if (selectedIds.length < MIN_AVAILABLE_SPONSOR_TASKS) {
      return;
    }
    setStep("quantity");
  };

  const handleQuantitySubmit = async () => {
    if (maxQuantity <= 0) {
      setWarning(
        t("Please fill in a whole number of participants between 1 and {{max}}.", {
          max: maxParticipants,
        }),
      );
      return;
    }
    setWarning("");
    setRequirementsLoading(true);
    const committee = committeeIGs
      .map((ig) => ig.trim())
      .filter((ig) => ig && ig !== "@");
    const tasks = await GetSponsorTaskRequirementsAPI({
      sponsorId: sponsor?.id ?? "",
      participants: participantsQuantity ?? 0,
      type: redeemType,
      officialIG,
      committeeIGs: redeemType === "society" ? committee : undefined,
    });
    setRequirementsLoading(false);
    if (!tasks) {
      setWarning(t("Could not load the sponsor obligations. Please try again."));
      return;
    }
    // Re-resolve against the count being confirmed, then narrow to the
    // student's actual selection. If the list changed underneath them, send
    // them back to the list so the reason is visible under the tasks rather than
    // as a second, generic warning at the bottom.
    setPreviewTasks(tasks);
    const available = tasks.filter(
      (task) => task.available !== false && task.id,
    );
    const availableIds = available.map((task) => String(task.id));
    setSelectedTaskIds((prev) =>
      prev === null ? null : prev.filter((id) => availableIds.includes(id)),
    );
    const selectedIds =
      selectedTaskIds === null
        ? availableIds
        : selectedTaskIds.filter((id) => availableIds.includes(id));
    if (selectedIds.length < MIN_AVAILABLE_SPONSOR_TASKS) {
      setStep("activity");
      return;
    }
    const selected = new Set(selectedIds);
    setAgreementTasks(
      tasks.filter(
        (task) =>
          task.available !== false &&
          task.id &&
          selected.has(String(task.id)),
      ),
    );
    setShowAgreement(true);
  };

  const handleAgreementSigned = async (signedAgreement: string) => {
    setShowAgreement(false);
    if (!sponsor) return;
    setBusy(true);
    setWarning("");
    let igs = committeeIGs
      .map((ig) => ig.trim())
      .filter((ig) => ig && ig !== "@");
    if (igs.length > 0 && igs[igs.length - 1] === "@") igs = igs.slice(0, -1);
    // `committeeIGs` is left `undefined` for a personal redemption rather than
    // emptied. `RedeemSponsorAPI` copies the key over only when it is truthy,
    // and `[]` is truthy, so sending an empty list would put a committee field
    // on a personal redemption that has none. The server normalises whatever
    // arrives through `parseCommitteeIgAccounts`, so it would store `null` for
    // `[]` too — but omitting the key is what keeps "personal" explicit at the
    // API boundary, and it is the shape the API documents.
    const resp = await RedeemSponsorAPI(sponsor.id, redeemType, {
      activityName,
      activityDate,
      signedAgreement,
      officialIG: officialIG.trim(),
      committeeIGs: redeemType === "society" ? igs : undefined,
      participantsQuantity: participantsQuantity ?? undefined,
      quantity: effectiveQuantity || undefined,
      taskIds: effectiveSelectedTaskIds,
      ...(redeemType === "society" ? { society_id: selectedSociety?.id } : {}),
    });
    setBusy(false);
    // Every status the redemption endpoint can actually return is resolved through
    // one shared table, so a refusal is never swallowed into one dead "try again"
    // string and this screen cannot drift from the app's. Anything with no copy of
    // its own is logged with the whole response, and the server's own message is
    // preferred over the generic fallback when it carries one.
    if (resp?.status === 0) {
      onRedeemed(0);
      onClose();
      return;
    }
    if (isAuthEnvelopeStatus(resp?.status)) {
      // `service.call` already raised a native alert for these and logged the raw
      // reason, so adding an inline warning here would only double up on screen.
      console.error("RedeemSponsorAPI auth envelope", resp);
      return;
    }
    if (!redeemSponsorMessageKey(resp?.status) && !redeemSponsorServerMessage(resp)) {
      console.error("RedeemSponsorAPI unhandled response", resp);
    }
    setWarning(t(redeemSponsorFailureKey(resp)));
  };

  const canAddCommitteeIG =
    committeeIGs.length < MAX_COMMITTEE_IGS &&
    committeeIGs.length > 0 &&
    committeeIGs.slice(0, -1).every((ig) => ig.trim() && ig.trim() !== "@") &&
    Boolean(committeeIGs[committeeIGs.length - 1].trim()) &&
    committeeIGs[committeeIGs.length - 1].trim() !== "@";

  const filteredSocieties = useMemo(() => {
    const query = societyQuery.trim().toLowerCase();
    if (!query) return allSocieties;
    return allSocieties.filter((society) =>
      society.name.toLowerCase().includes(query),
    );
  }, [allSocieties, societyQuery]);

  const societyTotalPages = Math.max(
    1,
    Math.ceil(filteredSocieties.length / SOCIETY_PAGE_SIZE),
  );
  /**
   * Clamped during render so a shrinking result set (a narrower search, a
   * reload) can never strand the student on a page past the end. 1,228 rows at
   * `SOCIETY_PAGE_SIZE` 20 gives 62 pages, the last one holding 8 rows.
   */
  const currentSocietyPage = Math.min(societyPage, societyTotalPages);
  const societyPageStart = (currentSocietyPage - 1) * SOCIETY_PAGE_SIZE;
  const pagedSocieties = filteredSocieties.slice(
    societyPageStart,
    societyPageStart + SOCIETY_PAGE_SIZE,
  );
  const societyPageEnd = societyPageStart + pagedSocieties.length;

  // A new query always starts from the top of the directory.
  useEffect(() => {
    setSocietyPage(1);
  }, [societyQuery]);

  // Land at the top of the box whenever the page changes.
  useEffect(() => {
    societyListRef.current?.scrollTo({ top: 0 });
  }, [currentSocietyPage, societyQuery]);

  const renderSocietyRow = (society: SponsorSociety) => {
    const isMember = mySocieties.some((item) => item.id === society.id);
    const isPending = pendingIds.includes(society.id);
    const isJoining = joiningSocietyId === society.id;

    const handleSelect = () => {
      if (isMember) {
        handleSocietySelected({ ...society, membership_id: "" });
      } else if (isPending) {
        setPendingJoinOnly(true);
        setPendingJoinSociety(society as SearchSociety);
      } else if (pendingIds.length > 0) {
        setWarning(
          t(
            "You already have a pending membership request. Please wait for approval.",
          ),
        );
      } else {
        setPendingJoinOnly(false);
        setPendingJoinSociety(society as SearchSociety);
      }
    };

    return (
      <div
        key={society.id}
        className={`flex w-full items-stretch gap-3 border-b border-gray-100 px-1 py-2.5 last:border-b-0 ${
          isJoining ? "opacity-60" : ""
        }`}
      >
        <button
          type="button"
          disabled={isJoining}
          onClick={handleSelect}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <SocietyAvatar
            icon={society.icon ?? undefined}
            name={society.name}
            className="h-10 w-10 shrink-0 text-sm"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900">
              {society.name}
            </p>
            {society.university && (
              <p className="truncate text-xs text-gray-500">
                {society.university}
              </p>
            )}
          </div>
        </button>
        {/*
          One fixed-width, fixed-height action column for all three states, so
          the row height (60px) never changes as rows are scrolled.
        */}
        <div className="flex w-[86px] shrink-0 items-center justify-end">
          {isJoining ? (
            <Loader2 size={16} className="animate-spin text-[#ffa217]" />
          ) : isMember ? (
            <span className="inline-flex h-6 items-center gap-1 text-[11px] font-semibold text-[#ffa217]">
              <BadgeCheck size={14} />
              {t("Member")}
            </span>
          ) : isPending ? (
            <span className="inline-flex h-6 items-center gap-1 text-[11px] font-semibold text-gray-500">
              <Clock size={14} />
              {t("Pending")}
            </span>
          ) : (
            <button
              type="button"
              disabled={isJoining}
              onClick={handleSelect}
              className="inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-[#ffa217] bg-white px-2.5 text-[11px] font-semibold text-[#ffa217] transition hover:bg-[#fff8f0]"
            >
              <Plus size={13} />
              {t("Join")}
            </button>
          )}
        </div>
      </div>
    );
  };

  const title = (() => {
    if (step === "type") return t("Redeem as:");
    if (step === "uploadDocs") return t("Upload your documents");
    if (step === "societyRegistration") {
      return regSubmitted ? t("Society registered!") : t("Register new society");
    }
    if (step === "societyPicker" && pendingJoinSociety) {
      return pendingJoinOnly
        ? t("Membership Request Sent")
        : t("Confirm Society");
    }
    if (step === "societyPicker") return t("Select your society");
    if (step === "quantity") return t("Choose quantity");
    if (redeemType === "society" && selectedSociety) {
      return t("Redeem as: {{societyName}}", {
        societyName: selectedSociety.name,
      });
    }
    return t("Fill in activity details");
  })();

  return (
    <>
      <Modal
        visible={visible && !showAgreement}
        onClose={onClose}
        panelClassName="mx-4 w-full max-w-[34rem]"
      >
        <div className="flex max-h-[88vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
            <h2 className="text-sm font-extrabold text-gray-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100"
              aria-label={t("Close")}
            >
              <X size={16} />
            </button>
          </div>

          {/* `min-h-0`: a flex item defaults to `min-height: auto`, so the body
              cannot shrink below its content and the column's `overflow-hidden`
              clips the overflow instead of letting it scroll. A bare `//` here
              is a JSX text child, so it renders as a paragraph in the body. */}
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
            {/* The type step is unreachable: the modal opens on the society
                picker. It is kept, with "Society" as the only option, so the flow
                can be brought back without rebuilding the step. */}
            {step === "type" && (
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  disabled={busy}
                  onClick={handleSocietyPress}
                  className="flex flex-col items-center gap-2 rounded-2xl border border-[#ffa217] px-4 py-6 text-sm font-bold text-[#ffa217] transition hover:bg-[#fff8f0] disabled:opacity-60"
                >
                  <Users size={20} />
                  {t("Society")}
                </button>
              </div>
            )}

            {step === "uploadDocs" && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  {t(
                    "Upload your HKID and student card to verify your identity before redeeming as an individual.",
                  )}
                </p>
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-gray-300 px-3 py-3 text-xs font-semibold text-gray-600">
                  <ImagePlus size={18} className="text-[#ffa217]" />
                  {hkidImage ? t("HKID selected") : t("Tap to upload HKID")}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) =>
                      pickImageInto(event.target.files?.[0], setHkidImage)
                    }
                  />
                </label>
                {hkidImage && (
                  <img
                    src={hkidImage}
                    alt={t("HKID")}
                    className="h-24 w-full rounded-lg object-cover"
                  />
                )}
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-gray-300 px-3 py-3 text-xs font-semibold text-gray-600">
                  <ImagePlus size={18} className="text-[#ffa217]" />
                  {studentCardImage
                    ? t("Student card selected")
                    : t("Tap to upload student card")}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) =>
                      pickImageInto(event.target.files?.[0], setStudentCardImage)
                    }
                  />
                </label>
                {studentCardImage && (
                  <img
                    src={studentCardImage}
                    alt={t("Student card")}
                    className="h-24 w-full rounded-lg object-cover"
                  />
                )}
              </div>
            )}

            {step === "societyPicker" && !pendingJoinSociety && (
                <div className="space-y-3">
                  {pendingIds.length > 0 && (
                    <div className="flex items-start gap-2 rounded-xl bg-orange-50 px-3 py-2 text-[11px] font-semibold text-orange-700">
                      <AlertCircle size={14} className="mt-0.5 shrink-0" />
                      {t(
                        "You have a pending membership request. Join another society after it is approved.",
                      )}
                    </div>
                  )}
                  <div className="mb-2 flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2">
                    <Search size={15} className="text-gray-400" />
                    <input
                      type="text"
                      value={societyQuery}
                      onChange={(event) => setSocietyQuery(event.target.value)}
                      placeholder={t("Search societies...")}
                      className="w-full bg-transparent text-sm outline-none"
                    />
                    {societyQuery && (
                      <button
                        type="button"
                        onClick={() => setSocietyQuery("")}
                        aria-label={t("Close")}
                      >
                        <X size={15} className="text-gray-400" />
                      </button>
                    )}
                  </div>
                  {mySocieties.length > 0 && (
                    <div>
                      <p className="mb-1 px-1 text-[11px] font-bold text-gray-500">
                        {t("My Societies")}
                      </p>
                      {mySocieties.map((society) => renderSocietyRow(society))}
                    </div>
                  )}
                  {mySocieties.length === 0 && (
                    <div className="flex items-start gap-2 rounded-xl bg-gray-50 px-3 py-3 text-xs text-gray-600">
                      <Info size={14} className="mt-0.5 shrink-0 text-slate-500" />
                      <span>
                        {t(
                          "You are not a member of any society yet — join one to redeem as a society.",
                        )}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="mb-1 px-1 text-[11px] font-bold text-gray-500">
                      {t("All Societies")}
                    </p>
                    {filteredSocieties.length === 0 ? (
                      <p className="py-3 text-center text-xs text-gray-400">
                        {t("No societies found.")}
                      </p>
                    ) : (
                      <>
                        <div
                          ref={societyListRef}
                          className="max-h-56 overflow-y-auto"
                        >
                          {pagedSocieties.map((society) =>
                            renderSocietyRow(society),
                          )}
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <p className="text-[11px] font-semibold text-gray-500">
                            {t(
                              "Showing {{from}}–{{to}} of {{total}}",
                              {
                                from: societyPageStart + 1,
                                to: societyPageEnd,
                                total: filteredSocieties.length,
                              },
                            )}
                          </p>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() =>
                                setSocietyPage(currentSocietyPage - 1)
                              }
                              disabled={currentSocietyPage <= 1}
                              aria-label={t("Previous page")}
                              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-[#fff8f0] hover:text-[#ffa217] disabled:text-gray-300"
                            >
                              <ChevronLeft size={18} />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setSocietyPage(currentSocietyPage + 1)
                              }
                              disabled={currentSocietyPage >= societyTotalPages}
                              aria-label={t("Next page")}
                              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-[#fff8f0] hover:text-[#ffa217] disabled:text-gray-300"
                            >
                              <ChevronRight size={18} />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep("societyRegistration")}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#ffa217] py-2.5 text-sm font-bold text-[#ffa217]"
                  >
                    <Plus size={16} />
                    {t("Add new society")}
                  </button>
                </div>
              )}

            {step === "societyPicker" && pendingJoinSociety && (
              <div className="space-y-3 text-center">
                <SocietyAvatar
                  icon={pendingJoinSociety.icon ?? undefined}
                  name={pendingJoinSociety.name}
                  className="h-14 w-14 text-xl"
                />
                <p className="text-sm font-bold text-gray-900">
                  {pendingJoinSociety.name}
                </p>
                {pendingJoinSociety.university && (
                  <p className="text-xs text-gray-500">
                    {pendingJoinSociety.university}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  {pendingJoinOnly
                    ? t(
                        "Your membership request is already under review. Please wait for a society member to approve.",
                      )
                    : t(
                        "Send membership request to this society? A society member will review and approve.",
                      )}
                </p>
                <div className="flex justify-center gap-3 pt-2">
                  {pendingJoinOnly ? (
                    <button
                      type="button"
                      onClick={() => {
                        setPendingJoinSociety(null);
                        setPendingJoinOnly(false);
                      }}
                      className="rounded-xl bg-[#ffa217] px-6 py-2 text-sm font-bold text-white"
                    >
                      {t("OK")}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          const target = pendingJoinSociety;
                          setPendingJoinSociety(null);
                          handleJoinSociety(target);
                        }}
                        className="rounded-xl bg-[#ffa217] px-6 py-2 text-sm font-bold text-white"
                      >
                        {t("Confirm")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingJoinSociety(null)}
                        className="rounded-xl px-4 py-2 text-sm font-bold text-gray-500"
                      >
                        {t("Cancel")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {step === "societyRegistration" &&
              (regSubmitted ? (
                <div className="space-y-3 text-center">
                  <p className="text-sm font-bold text-green-600">
                    {regVerified
                      ? t(
                          "Your society registration has been submitted for admin approval. You may proceed with the redemption.",
                        )
                      : t(
                          "Your society registration has been submitted for admin approval. Your membership will be reviewed by an admin. You can redeem as this society once approved.",
                        )}
                  </p>
                  {!regVerified && (
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-xl bg-[#ffa217] px-6 py-2 text-sm font-bold text-white"
                    >
                      {t("OK")}
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={regName}
                    onChange={(event) => setRegName(event.target.value)}
                    placeholder={t("Society name *")}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#ffa217]"
                  />
                  <select
                    value={regUniversity}
                    onChange={(event) => setRegUniversity(event.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#ffa217]"
                  >
                    <option value="">{t("Select university")}</option>
                    {universityList.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={regInstagram}
                    onChange={(event) => setRegInstagram(event.target.value)}
                    placeholder={t("Instagram URL")}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#ffa217]"
                  />
                  {(
                    [
                      {
                        label: t("Upload Society Icon (optional)"),
                        value: regIcon,
                        apply: setRegIcon,
                        picked: t("Icon selected"),
                        idle: t("Tap to upload icon"),
                      },
                      {
                        label: t("Upload HKID *"),
                        value: regHkid,
                        apply: setRegHkid,
                        picked: t("HKID selected"),
                        idle: t("Tap to upload HKID"),
                      },
                      {
                        label: t("Upload Student Card *"),
                        value: regStudentCard,
                        apply: setRegStudentCard,
                        picked: t("Student card selected"),
                        idle: t("Tap to upload student card"),
                      },
                      {
                        label: t("Upload Society Chop (optional)"),
                        value: regChop,
                        apply: setRegChop,
                        picked: t("Chop selected"),
                        idle: t("Tap to upload society chop"),
                      },
                    ]
                  ).map((field) => (
                    <div key={field.label}>
                      <p className="mb-1 text-[11px] font-bold text-[#ffa217]">
                        {field.label}
                      </p>
                      <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600">
                        <Upload size={16} className="text-[#ffa217]" />
                        {field.value ? field.picked : field.idle}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(event) =>
                            pickImageInto(event.target.files?.[0], field.apply)
                          }
                        />
                      </label>
                    </div>
                  ))}
                </div>
              ))}

            {step === "activity" && (
              <div className="space-y-3">
                <input
                  type="text"
                  value={activityName}
                  onChange={(event) => setActivityName(event.target.value)}
                  placeholder={t("Activity name for sponsor")}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#ffa217]"
                />
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2">
                  <Calendar size={15} className="text-gray-400" />
                  <input
                    type="date"
                    value={activityDate}
                    onChange={(event) => setActivityDate(event.target.value)}
                    min={getEarliestSelectableDateString()}
                    className="w-full bg-transparent text-sm accent-[#ffa217] outline-none focus:border-[#ffa217]"
                  />
                </div>
                <div>
                  <p className="mb-1 text-[11px] font-bold text-[#ffa217]">
                    {t("Participants")}
                  </p>
                  <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2">
                    <Users size={15} className="text-gray-400" />
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={maxParticipants}
                      value={participants}
                      onChange={(event) =>
                        handleParticipantsChange(event.target.value)
                      }
                      className="w-full bg-transparent text-sm outline-none"
                    />
                  </div>
                  {/* No "Max participants" hint: the bound is now the
                      `participants_quantity` column's Int capacity, so printing it
                      would show a database detail as if it were a product rule. */}
                </div>
                <div>
                  <p className="mb-1 text-[11px] font-bold text-[#ffa217]">
                    {t("Official society Instagram account")}
                  </p>
                  <div className="flex items-center gap-1 rounded-xl bg-gray-50 px-3 py-2">
                    <span className="text-sm font-bold text-gray-400">@</span>
                    <input
                      type="text"
                      value={officialIG.replace(/^@/, "")}
                      onChange={(event) =>
                        setOfficialIG(
                          "@" + event.target.value.replace(/^@+/, ""),
                        )
                      }
                      placeholder={t("Instagram account")}
                      className="w-full bg-transparent text-sm outline-none"
                    />
                  </div>
                </div>
                {redeemType === "personal" && (
                  <p className="mt-1 text-[11px] text-gray-500">
                    {t(
                      "Committee IG accounts are only required when redeeming as a society.",
                    )}
                  </p>
                )}
                {redeemType === "society" && (
                  <div>
                    <p className="mb-1 text-[11px] font-bold text-[#ffa217]">
                      {t("Committee IG Accounts (at least 5)")}
                    </p>
                    <div className="space-y-2">
                      {committeeIGs.map((account, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="flex flex-1 items-center gap-1 rounded-xl bg-gray-50 px-3 py-2">
                            <span className="text-sm font-bold text-gray-400">@</span>
                            <input
                              type="text"
                              value={account.replace(/^@/, "")}
                              onChange={(event) => {
                                const next = [...committeeIGs];
                                next[index] =
                                  "@" + event.target.value.replace(/^@+/, "");
                                setCommitteeIGs(next);
                              }}
                              placeholder={t("Instagram account")}
                              className="w-full bg-transparent text-sm outline-none"
                            />
                          </div>
                          {committeeIGs.length > MIN_COMMITTEE_IGS && (
                            <button
                              type="button"
                              onClick={() =>
                                setCommitteeIGs(
                                  committeeIGs.filter((_, i) => i !== index),
                                )
                              }
                              aria-label={t("Remove")}
                              className="text-red-500"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      disabled={!canAddCommitteeIG}
                      onClick={() => setCommitteeIGs([...committeeIGs, "@"])}
                      className="mt-2 self-end rounded-lg bg-[#ffa217] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                    >
                      {t("Add Committee Account")}
                    </button>
                  </div>
                )}
                {sponsorTasks.length > 0 && (
                  <div className="rounded-xl bg-gray-50 px-3 py-2">
                    <p className="mb-1 text-[11px] font-bold text-gray-500">
                      {t("Sponsor Tasks")}
                    </p>
                    <ol className="list-decimal space-y-0.5 pl-4 text-[11px] text-gray-600">
                      {/* Resolved copy once it arrives, because the catalog copy
                          carries an unfilled `{number}` placeholder. Both branches go
                          through the same renderer, so a title can never reach the
                          screen with its placeholder showing. */}
                      {displayedTasks.map((task: any, index: number) => {
                        const unavailable = task.available === false;
                        const taskId = task.id
                          ? String(task.id)
                          : undefined;
                        const selectable = !unavailable && Boolean(taskId);
                        const selected =
                          selectable &&
                          effectiveSelectedTaskIds.includes(taskId as string);
                        const conflicts = (task.unavailable_conflicts ?? [])
                          .map((conflict: any) => ({
                            handles: (conflict.handles ?? [])
                              .map((handle: string) => `@${handle}`)
                              .join(", "),
                            sponsor: conflict.sponsor_name,
                          }))
                          .filter((conflict: any) => conflict.handles);
                        return (
                          <li
                            key={taskId || `${task.title}-${index}`}
                            className={selected ? "rounded bg-orange-50" : ""}
                          >
                            <button
                              type="button"
                              role="checkbox"
                              aria-checked={selected}
                              aria-label={`${index + 1}. ${describeSponsorTaskLine(task)}`}
                              disabled={!selectable}
                              onClick={() => handleToggleTask(taskId)}
                              className={`flex w-full items-start gap-2 py-0.5 text-left ${
                                selectable ? "cursor-pointer" : "cursor-not-allowed opacity-75"
                              }`}
                            >
                              <span
                                className={`mt-[2px] flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                  selected
                                    ? "border-[#ffa217] bg-[#ffa217] text-white"
                                    : "border-gray-300 bg-white"
                                }`}
                              >
                                {selected && <Check size={11} strokeWidth={3} />}
                              </span>
                              <span
                                className={`min-w-0 flex-1 ${
                                  unavailable
                                    ? "text-gray-400 line-through decoration-gray-300"
                                    : ""
                                }`}
                              >
                                {describeSponsorTaskLine(task)}
                                {unavailable && (
                                  <span className="mt-0.5 block text-[10px] font-semibold text-red-500 no-underline">
                                    {conflicts.length > 0
                                      ? t(
                                          "Unavailable: {{handles}} already used for {{sponsor}}.",
                                          {
                                            handles: conflicts
                                              .map((conflict: any) => conflict.handles)
                                              .join("; "),
                                            sponsor:
                                              conflicts
                                                .map((conflict: any) => conflict.sponsor)
                                                .filter(Boolean)
                                                .join(", ") || "—",
                                          },
                                        )
                                      : t("Unavailable for this redemption.")}
                                  </span>
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ol>
                    {availableCount < MIN_AVAILABLE_SPONSOR_TASKS &&
                      availabilityWarning && (
                        <p className="mt-2 rounded-lg bg-red-50 px-2 py-1.5 text-[11px] font-semibold text-red-600">
                          {availabilityWarning}
                        </p>
                      )}
                    {availableCount >= MIN_AVAILABLE_SPONSOR_TASKS &&
                      selectedCount < MIN_AVAILABLE_SPONSOR_TASKS && (
                        <p className="mt-2 rounded-lg bg-red-50 px-2 py-1.5 text-[11px] font-semibold text-red-600">
                          {t("Select at least 4 sponsor tasks.")}
                        </p>
                      )}
                  </div>
                )}
              </div>
            )}

            {step === "quantity" && (
              <div className="space-y-3">
                <div className="rounded-xl bg-gray-50 px-3 py-2.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-gray-600">
                      {t("Participants")}
                    </span>
                    <span className="font-extrabold text-gray-900">
                      {participantsQuantity ?? 0}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs">
                    <span className="font-semibold text-gray-600">
                      {t("Max quantity")}
                    </span>
                    <span className="font-extrabold text-gray-900">
                      {maxQuantity}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="mb-1 text-[11px] font-bold text-[#ffa217]">
                    {t("Quantity (packs as priced)")}
                  </p>
                  <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(effectiveQuantity - 1)}
                      disabled={effectiveQuantity <= 1}
                      aria-label={t("Decrease quantity")}
                      className="rounded-lg p-1 text-gray-500 disabled:text-gray-300"
                    >
                      <Minus size={15} />
                    </button>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={maxQuantity}
                      value={effectiveQuantity}
                      onChange={(event) =>
                        handleQuantityChange(Number(event.target.value))
                      }
                      className="w-full bg-transparent text-center text-sm outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(effectiveQuantity + 1)}
                      disabled={effectiveQuantity >= maxQuantity}
                      aria-label={t("Increase quantity")}
                      className="rounded-lg p-1 text-gray-500 disabled:text-gray-300"
                    >
                      <Plus size={15} />
                    </button>
                  </div>
                </div>

                <p className="text-[11px] leading-relaxed text-gray-500">
                  {t(
                    "You can claim up to {{maxQuantity}} for {{participants}} participants.",
                    {
                      maxQuantity,
                      participants: participantsQuantity ?? 0,
                    },
                  )}
                </p>
              </div>
            )}

            {warning && (
              <p className="flex items-start gap-1.5 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                {warning}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-5 py-3">
            {step === "quantity" ? (
              <button
                type="button"
                onClick={() => setStep("activity")}
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold text-gray-500"
              >
                <ArrowLeft size={15} />
                {t("Back")}
              </button>
            ) : step === "activity" ? (
              <button
                type="button"
                onClick={() => {
                  // Back to the society picker, not the hidden type step.
                  setSelectedSociety(null);
                  setRedeemType("society");
                  setStep("societyPicker");
                }}
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold text-gray-500"
              >
                <ArrowLeft size={15} />
                {t("Back")}
              </button>
            ) : step === "societyRegistration" ? (
              <button
                type="button"
                onClick={() => setStep("societyPicker")}
                className="rounded-xl px-3 py-2 text-sm font-bold text-gray-500"
              >
                {t("Back")}
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-3 py-2 text-sm font-bold text-gray-500"
              >
                {t("Cancel")}
              </button>
            )}

            {step === "quantity" && (
              <button
                type="button"
                onClick={handleQuantitySubmit}
                disabled={requirementsLoading}
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-6 py-2 text-sm font-bold text-white transition hover:bg-[#f59200] disabled:opacity-60"
              >
                {requirementsLoading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Check size={15} />
                )}
                {t("Confirm")}
              </button>
            )}
            {step === "activity" && (
              <button
                type="button"
                onClick={handleActivitySubmit}
                disabled={
                  requirementsLoading ||
                  availableCount < MIN_AVAILABLE_SPONSOR_TASKS ||
                  selectedCount < MIN_AVAILABLE_SPONSOR_TASKS
                }
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-6 py-2 text-sm font-bold text-white transition hover:bg-[#f59200] disabled:opacity-60"
              >
                {requirementsLoading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Check size={15} />
                )}
                {requirementsLoading ? t("Checking...") : t("Confirm")}
              </button>
            )}
            {step === "societyRegistration" && !regSubmitted && (
              <button
                type="button"
                onClick={handleRegisterSociety}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-6 py-2 text-sm font-bold text-white disabled:opacity-60"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                {busy ? t("Uploading...") : t("Register")}
              </button>
            )}
            {step === "uploadDocs" && (
              <button
                type="button"
                onClick={handleUploadDocs}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-6 py-2 text-sm font-bold text-white disabled:opacity-60"
              >
                {busy ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Building2 size={15} />
                )}
                {t("Upload")}
              </button>
            )}
          </div>
        </div>
      </Modal>

      <StudentSponsorAgreementModal
        visible={visible && step === "agreement"}
        onClose={() => setShowAgreement(false)}
        onSigned={handleAgreementSigned}
        sponsorName={sponsor?.name}
        sponsorDescription={sponsor?.description}
        provider={sponsor?.provider}
        sponsorTasks={agreementTasks}
        activityName={activityName}
        activityDate={activityDate}
        officialIG={officialIG}
        committeeIGs={committeeIGs}
        societyName={selectedSociety?.name}
        societyChopUrl={selectedSociety?.chop_image_url || undefined}
        redeemType={redeemType}
        participantsQuantity={participantsQuantity ?? undefined}
        quantity={effectiveQuantity}
        maxQuantity={maxQuantity}
        onUploadChop={redeemType === "society" ? handleUploadChop : undefined}
        chopUploading={chopUploading}
      />
    </>
  );
};

export default StudentSponsorRedeemModal;
